import { Component, ReactNode } from "react";

/** How long a crashed active slide stays black before the player moves on.
 *  Not instant: a playlist where every slide throws must not spin the CPU. */
export const SLIDE_ERROR_SKIP_MS = 1500;

interface SlideErrorBoundaryProps {
  /** Whether the wrapped slide is the visible one. */
  active: boolean;
  /** Advance the playlist. Only the active slide's boundary gets one. */
  onSkip?: () => void;
  children: ReactNode;
}

interface SlideErrorBoundaryState {
  failed: boolean;
}

/** Contains a crash to the one slide that threw: it renders black instead of
 *  taking down the whole player, and if it's on screen the playlist skips
 *  past it. Mount one per slide, keyed by slide id, so it resets per slide. */
export class SlideErrorBoundary extends Component<SlideErrorBoundaryProps, SlideErrorBoundaryState> {
  state: SlideErrorBoundaryState = { failed: false };
  private skipTimer: ReturnType<typeof setTimeout> | null = null;

  static getDerivedStateFromError(): SlideErrorBoundaryState {
    return { failed: true };
  }

  componentDidMount() {
    this.sync();
  }

  componentDidUpdate(prev: SlideErrorBoundaryProps) {
    if (this.state.failed && this.props.active && !prev.active) {
      // Coming back on screen after an earlier crash (e.g. a preloaded
      // neighbour that hit a transient error): give it one fresh try. If it
      // throws again, the next update schedules the skip.
      this.clearSkip();
      this.setState({ failed: false });
      return;
    }
    this.sync();
  }

  componentWillUnmount() {
    this.clearSkip();
  }

  private sync() {
    if (!this.props.active) {
      this.clearSkip();
      return;
    }
    if (this.state.failed && !this.skipTimer) {
      this.skipTimer = setTimeout(() => {
        this.skipTimer = null;
        if (this.props.active) this.props.onSkip?.();
      }, SLIDE_ERROR_SKIP_MS);
    }
  }

  private clearSkip() {
    if (this.skipTimer) clearTimeout(this.skipTimer);
    this.skipTimer = null;
  }

  render() {
    if (this.state.failed) return <div className="h-full w-full bg-black" />;
    return this.props.children;
  }
}
